// =============================================================================
// RÉFÉRENTIEL OFFICIEL CCS ECSR — Animer des actions de formation à la conduite
// en sécurité des véhicules motorisés à deux roues
// =============================================================================
// Source : Titre Professionnel TP-01303 — Arrêté du 6 avril 2016 (JO 28/04/2016)
// Modifié par arrêté du 25 juin 2018 (JO 04/07/2018)
// Ministère du Travail — Direction Générale du Travail
// =============================================================================

export const REFERENTIEL_META = {
  intitule: "Certificat Complémentaire de Spécialisation à l'enseignement de la conduite en sécurité des véhicules motorisés à deux roues",
  sigle: 'CCS ECSR 2 Roues',
  codeTP: 'TP-01303',
  dateJO: '28/04/2016',
  arrete: "Arrêté du 6 avril 2016 modifié par l'arrêté du 25 juin 2018",
  niveau: 'Niveau 5 (Bac+2)',
  codeNSF: '344 / 311',
  organismeCertif: 'Ministère du Travail',
  prerequisAdministratif: [
    "Être titulaire du Titre Professionnel ECSR (TP-00379) ou du BEPECASER mention deux-roues",
    "Permis B en cours de validité",
    "Permis A (ou A2 + passerelle) en cours de validité — minimum 2 ans pour l'animation circulation",
  ],
  duréeFormation: "245 heures minimum (théorique + pratique en école agréée DREAL)",
  validite: 'Permanente — recyclage tous les 5 ans recommandé',
};

// =============================================================================
// BLOC UNIQUE — DESCRIPTION
// =============================================================================
// Le CCS ECSR 2 roues comporte UN bloc de compétences, mais avec TROIS
// compétences distinctes faisant l'objet de TROIS modalités d'évaluation.
// =============================================================================

export const BLOC_COMPETENCES = {
  numero: 1,
  intitule: "Animer des actions de formation à la conduite en sécurité des véhicules motorisés à deux roues en circulation et hors circulation",
  description: "Le titulaire conçoit et anime des actions de formation à la conduite et à la sécurité, en lien avec la sécurité routière et l'éducation routière, à destination de publics variés (élèves de l'auto-école, formation continue, animation collective).",

  competences: [
    // ---- Compétence 1 ----------------------------------------------------
    {
      numero: 1,
      intitule: "Animer une séance de formation à la conduite en sécurité des véhicules motorisés à deux roues HORS CIRCULATION",
      description: "Préparer et conduire une séance pédagogique sur plateau (maniabilité, freinage, équilibre, technique) en garantissant la sécurité physique et l'apprentissage progressif des élèves.",
      savoirs: [
        "Réglementation REMC (Référentiel pour l'Éducation à une Mobilité Citoyenne) volet 2RM",
        "Compétences techniques attendues sur plateau (équilibre, maniabilité, freinage)",
        "Pédagogie de l'apprentissage moteur (Vygotski — ZPD)",
        "Sécurité physique sur plateau (distances, équipement, gestion du groupe)",
      ],
      savoirFaire: [
        "Préparer le matériel et le terrain (parcours, plots, signalisation)",
        "Adapter la séance au niveau des élèves (diagnostique)",
        "Démontrer une figure ou un exercice (modèle)",
        "Corriger en temps réel (feedback immédiat)",
        "Évaluer la progression (formative et sommative)",
      ],
      criteres: [
        "Pertinence des objectifs pédagogiques par rapport au niveau des élèves",
        "Sécurité du dispositif (placement, équipement, consignes)",
        "Clarté des consignes et démonstrations",
        "Qualité de l'observation et du feedback individuel",
        "Adaptation aux difficultés rencontrées",
      ],
      modaliteEval: 'MSP',
      duree: '50 minutes',
    },
    // ---- Compétence 2 ----------------------------------------------------
    {
      numero: 2,
      intitule: "Animer une séance de formation à la conduite en sécurité des véhicules motorisés à deux roues EN CIRCULATION",
      description: "Préparer et conduire une séance pédagogique en circulation réelle (ville, route, autoroute selon niveau) avec un élève accompagné, en garantissant la sécurité, l'apprentissage et la mobilité citoyenne.",
      savoirs: [
        "Code de la route — actualisation permanente",
        "REMC volet 2RM (compétences 1 à 9)",
        "Communication moto-élève (gestes, oreillette ou suiveuse + radio)",
        "Pédagogie de la guidance progressive (Bruner — étayage)",
        "Gestion du risque en formation (sélection du parcours adapté)",
      ],
      savoirFaire: [
        "Préparer un parcours adapté aux objectifs et au niveau",
        "Donner les consignes claires avant départ",
        "Suivre l'élève en moto-école avec radio ou en voiture",
        "Observer les défaillances et les forces de l'élève",
        "Faire un débriefing structuré post-séance (méthode du QFD : Quoi ? Pourquoi ? Comment faire mieux ?)",
      ],
      criteres: [
        "Pertinence du parcours par rapport aux objectifs",
        "Sécurité de l'organisation (espacement, communication, position)",
        "Qualité de la communication pendant la séance",
        "Capacité à observer et identifier les points clés",
        "Pertinence du débriefing et des préconisations",
      ],
      modaliteEval: 'MSP',
      duree: '60 minutes',
    },
    // ---- Compétence 3 ----------------------------------------------------
    {
      numero: 3,
      intitule: "Enseigner les spécificités de l'environnement de la circulation et de la conduite des véhicules motorisés à deux roues",
      description: "Maîtriser les contenus pédagogiques spécifiques aux 2RM (physique, équipement, réglementation, comportement, environnement) et savoir les transmettre à différents publics.",
      savoirs: [
        "Connaissance approfondie du véhicule 2RM (catégories L1e à L3e, équipement, transmissions, freinage ABS/CBS)",
        "Lois physiques en moto (gravité, centrifuge, contre-braquage, énergie cinétique)",
        "Réglementation 2RM complète (équipement, vitesse, contrôle technique 2024)",
        "Statistiques d'accidentologie 2RM (sources ONISR, MAIDS)",
        "Théories pédagogiques mobilisables (Knowles, Kolb, Vygotski, Bandura, Bloom, Reason, Rasmussen)",
      ],
      savoirFaire: [
        "Préparer une intervention sur un thème spécifique 2RM (étude de cas)",
        "Choisir le type d'intervention adapté (notions / enseigner / sensibiliser)",
        "Construire une progression pédagogique en 6 phases (accueil → bilan)",
        "Sélectionner les méthodes (démonstrative, interrogative, active, expositive, découverte)",
        "Préparer une évaluation cohérente (diagnostique, formative, sommative)",
        "Répondre aux questions techniques et pédagogiques du jury",
      ],
      criteres: [
        "Maîtrise technique du contenu (exactitude des références et chiffres)",
        "Clarté de la progression pédagogique",
        "Pertinence du choix méthodologique",
        "Qualité de l'argumentation pédagogique",
        "Posture professionnelle (déontologie, neutralité, écoute)",
      ],
      modaliteEval: 'ET',
      duree: '60 minutes (20 min préparation + 20 min présentation + 20 min entretien)',
    },
  ],
};

// =============================================================================
// MODALITÉS D'ÉVALUATION
// =============================================================================
export const MODALITES_EVALUATION = [
  {
    code: 'MSP',
    intitule: "Mise en Situation Professionnelle",
    duree: '1 h 50',
    description: "Le candidat anime DEUX séances réelles : 50 min hors circulation (plateau) + 1 h en circulation. Un jury de 2 personnes (formateurs ECSR, IPCSR) observe avec une grille critériée.",
    competencesEvaluees: [1, 2],
    composition: [
      { phase: 'Hors circulation (plateau)', duree: '50 min', detail: "Le candidat anime un exercice technique (slalom, freinage, maniabilité) à 1 ou 2 élèves." },
      { phase: 'En circulation', duree: '60 min', detail: "Le candidat anime une séance avec un élève (en moto-école derrière ou en voiture suiveuse selon configuration)." },
    ],
    documentsAttendus: ["Fiche de préparation de séance", "Grille d'évaluation de l'élève", "Plan du parcours (hors circulation)"],
    coefficient: 2,
  },
  {
    code: 'ET',
    intitule: "Entretien Technique (Étude de cas)",
    duree: '1 h 00',
    description: "Le candidat tire au sort un sujet parmi les études de cas officielles (27 sujets couvrant les 7 aspects). 20 min de préparation seul, 20 min de présentation devant le jury, 20 min de questions-réponses.",
    competencesEvaluees: [3],
    composition: [
      { phase: 'Préparation', duree: '20 min', detail: "Le candidat lit le sujet, structure sa séance sur papier (objectifs, déroulé, méthodes, évaluation)." },
      { phase: 'Présentation', duree: '20 min', detail: "Le candidat présente sa séance comme s'il s'adressait au public visé. Il peut utiliser un tableau, des schémas, des supports." },
      { phase: 'Entretien', duree: '20 min', detail: "Le jury pose des questions techniques (Code de la route, physique, équipement) et pédagogiques (justification des choix méthodologiques)." },
    ],
    documentsAttendus: ["Aucun document à apporter (tout est fourni : papier, stylos, accès à un tableau)"],
    coefficient: 2,
  },
  {
    code: 'EF',
    intitule: "Entretien Final",
    duree: '15 min',
    description: "Le jury évalue la compréhension globale du métier, la posture professionnelle, la déontologie et le projet professionnel du candidat.",
    competencesEvaluees: [1, 2, 3],
    composition: [
      { phase: 'Échange ouvert', duree: '15 min', detail: "Questions sur la vocation, la motivation, l'éthique du métier, la pratique réflexive, la formation continue." },
    ],
    documentsAttendus: ["Dossier professionnel (rapport d'activité ou portfolio)"],
    coefficient: 1,
  },
];

export const DUREE_TOTALE_EPREUVE = '3 h 05';

// =============================================================================
// VOIES D'ACCÈS À LA CERTIFICATION
// =============================================================================
export const VOIES_ACCES = [
  {
    code: 'initial',
    intitule: 'Voie de la formation initiale',
    description: "Suivi d'une formation de 245h dans une école agréée DREAL, puis passage des 3 épreuves.",
    duree: '245h sur 6 à 12 mois',
    cout: '4 500 € à 7 000 € selon école',
    financement: ['CPF (jusqu\'à 5 000 €)', 'France Travail (POEI, AIF)', 'OPCO employeur', 'Auto-financement'],
  },
  {
    code: 'vae',
    intitule: 'Validation des Acquis de l\'Expérience (VAE)',
    description: "Accessible avec 1 an d'expérience minimum dans l'animation de formations 2RM ou activités équivalentes. Constitution d'un dossier (livret 1 + livret 2) puis passage du jury.",
    duree: '6 à 12 mois (dossier + accompagnement)',
    cout: '500 € à 1 500 € (accompagnement)',
    financement: ['CPF', 'OPCO', 'France VAE'],
  },
  {
    code: 'equivalence',
    intitule: 'Équivalence / Passerelle',
    description: "Si possession du BEPECASER mention 2RM (ancienne certification) : équivalence partielle ou totale selon date d'obtention.",
    duree: 'Variable',
    cout: 'Variable',
    financement: ['Selon situation'],
  },
];

// =============================================================================
// COMPÉTENCES TRANSVERSALES (déontologie)
// =============================================================================
export const COMPETENCES_TRANSVERSALES = [
  {
    intitule: "Posture professionnelle",
    description: "Adopter une attitude bienveillante, neutre, exigeante et respectueuse vis-à-vis des élèves, collègues, jury et administration.",
    indicateurs: ["Ponctualité", "Tenue professionnelle", "Neutralité (politique, religieuse, marketing)", "Confidentialité"],
  },
  {
    intitule: "Déontologie",
    description: "Respecter le cadre légal et éthique du métier d'ECSR (égalité des chances, non-discrimination, refus de toute forme de complaisance).",
    indicateurs: ["Refus de toute fraude (évaluation, signature)", "Égalité de traitement entre élèves", "Signalement des situations à risque (élève dangereux, etc.)"],
  },
  {
    intitule: "Veille réglementaire et pédagogique",
    description: "Se tenir à jour des évolutions du Code de la route, du REMC, des techniques pédagogiques et de l'accidentologie.",
    indicateurs: ["Lecture des bulletins ONISR", "Participation à des formations continues (5 ans recommandé)", "Échange entre pairs"],
  },
];

// =============================================================================
// 7 ASPECTS PÉDAGOGIQUES (selon référentiel officiel d'études de cas CCS)
// =============================================================================
export const SEPT_ASPECTS = [
  {
    code: 'vehicule',
    intitule: 'Connaissance du véhicule',
    description: "Catégories L1e à L3e, mécanique (transmission, freinage), équipement, entretien, contrôle technique 2RM.",
  },
  {
    code: 'physique',
    intitule: 'Aspect physique',
    description: "Lois mécaniques (gravité, énergie cinétique ½mv², force centrifuge), contre-braquage, équilibre dynamique, adhérence.",
  },
  {
    code: 'reglementation',
    intitule: 'Réglementation et signalisation',
    description: "Code de la route, signalisation horizontale/verticale, sanctions, contrôle technique, réglementation européenne.",
  },
  {
    code: 'statistique',
    intitule: 'Facteur de risque statistique',
    description: "Données ONISR, étude MAIDS, accidentologie 2RM, profils de risque, comparaison avec voitures.",
  },
  {
    code: 'comportemental',
    intitule: 'Comportemental',
    description: "Influence sociale, pression des pairs, attitudes au volant, gestion du groupe, conflits.",
  },
  {
    code: 'physiologique',
    intitule: 'Physiologique et psychologique',
    description: "Alcool, drogues, médicaments, fatigue, stress, vieillissement, vision.",
  },
  {
    code: 'social',
    intitule: 'Conscientisation et approche sociale',
    description: "Surestimation (Dunning-Kruger), fatalité, locus de contrôle (Rotter), risque accepté, motivations.",
  },
];
